import React from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { COLORS } from './styles'
import { logger } from '@utils'

// Custom toast styles for different types
const toastStyles = {
    success: {
        background: COLORS.SUCCESS.background,
        border: `1px solid ${COLORS.SUCCESS.border}`,
        color: COLORS.SUCCESS.text,
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    error: {
        background: COLORS.ERROR.background,
        border: `1px solid ${COLORS.ERROR.border}`,
        color: COLORS.ERROR.text,
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    info: {
        background: COLORS.INFO.background,
        border: `1px solid ${COLORS.INFO.border}`,
        color: COLORS.INFO.text,
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    warning: {
        background: COLORS.WARNING.background,
        border: `1px solid ${COLORS.WARNING.border}`,
        color: COLORS.WARNING.text,
        borderRadius: '8px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
}

// Toast close button component
const ToastCloseButton = ({ toastId }) => {
    return (
        <button
            onClick={() => toast.dismiss(toastId)}
            style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                color: 'gray',
                opacity: 0.7,
                padding: '0',
                marginLeft: '8px',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center',
                fontWeight: '500',
            }}
            aria-label="Close"
        >
            close
        </button>
    )
}

// Toast content component with icon, message, and close button
const ToastContent = ({ icon, message, toastId, isShowCloseButton = true, closeButton }) => {
    const renderCloseButton = () => {
        if (closeButton) {
            // If closeButton is a function, call it with close function; otherwise render it directly
            if (typeof closeButton === 'function') {
                const close = () => toast.dismiss(toastId)
                return closeButton(close)
            }
            return closeButton
        }
        if (isShowCloseButton) {
            return <ToastCloseButton toastId={toastId} />
        }
        return null
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {icon}
            <span style={{ flex: 1 }}>{message}</span>
            {renderCloseButton()}
        </div>
    )
}

// Toast event handlers - these are called globally for all toasts
export const toastEventHandlers = {
    onOpen: toastId => {
        // Handle toast open event
        // You can add custom logic here, e.g., analytics tracking
        logger.debug('Toast opened:', toastId)
    },
    onClose: toastId => {
        // Handle toast close event
        // You can add custom logic here, e.g., analytics tracking
        logger.debug('Toast closed:', toastId)
    },
    onDismiss: toastId => {
        // Handle toast dismiss event
        logger.info('Toast dismissed:', toastId)
    },
}

// Custom toast functions with predefined styles
export const customToast = {
    success: (message, options = {}) => {
        const defaultIcon = <i className="bx bx-check-circle bx-sm"></i>
        const { icon = defaultIcon, isShowCloseButton = true, closeButton, ...restOptions } = options
        const toastId = toast(
            t => (
                <ToastContent
                    icon={icon}
                    message={message}
                    toastId={t.id}
                    isShowCloseButton={isShowCloseButton}
                    closeButton={closeButton}
                />
            ),
            {
                style: toastStyles.success,
                duration: options.duration || 3000,
                ...restOptions,
            }
        )
        return toastId
    },
    error: (message, options = {}) => {
        const defaultIcon = <i className="bx bx-x-circle bx-sm"></i>
        const { icon = defaultIcon, isShowCloseButton = true, closeButton, ...restOptions } = options
        const toastId = toast(
            t => (
                <ToastContent
                    icon={icon}
                    message={message}
                    toastId={t.id}
                    isShowCloseButton={isShowCloseButton}
                    closeButton={closeButton}
                />
            ),
            {
                style: toastStyles.error,
                duration: options.duration || 4000,
                ...restOptions,
            }
        )
        return toastId
    },
    info: (message, options = {}) => {
        const defaultIcon = <i className="bx bx-info-circle bx-sm"></i>
        const { icon = defaultIcon, isShowCloseButton = true, closeButton, ...restOptions } = options
        const toastId = toast(
            t => (
                <ToastContent
                    icon={icon}
                    message={message}
                    toastId={t.id}
                    isShowCloseButton={isShowCloseButton}
                    closeButton={closeButton}
                />
            ),
            {
                style: toastStyles.info,
                duration: options.duration || 3000,
                ...restOptions,
            }
        )
        return toastId
    },
    warning: (message, options = {}) => {
        const defaultIcon = <i className="bx bx-error bx-sm"></i>
        const { icon = defaultIcon, isShowCloseButton = true, closeButton, ...restOptions } = options
        const toastId = toast(
            t => (
                <ToastContent
                    icon={icon}
                    message={message}
                    toastId={t.id}
                    isShowCloseButton={isShowCloseButton}
                    closeButton={closeButton}
                />
            ),
            {
                style: toastStyles.warning,
                duration: options.duration || 3000,
                ...restOptions,
            }
        )
        return toastId
    },
    // Toast with ToastContent (close button) but using default style (maintains original toast appearance)
    plain: (message, options = {}) => {
        const { icon, isShowCloseButton = true, closeButton, ...restOptions } = options
        const toastId = toast(
            t => (
                <ToastContent
                    icon={icon}
                    message={message}
                    toastId={t.id}
                    isShowCloseButton={isShowCloseButton}
                    closeButton={closeButton}
                />
            ),
            {
                duration: options.duration || 3000,
                ...restOptions,
            }
        )
        return toastId
    },
    // Keep the default toast function for custom use cases
    default: toast,
}

// Toaster component configuration
export const toasterConfig = {
    position: 'top-center',
    reverseOrder: false,
    gutter: 8,
    containerClassName: '',
    containerStyle: {},
    // Global event handlers for all toasts
    ...toastEventHandlers,
    toastOptions: {
        // Default duration
        duration: 3000,
        // Default style
        style: {
            borderRadius: '8px',
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: '500',
        },
        // Success toast default options
        success: {
            duration: 3000,
            style: toastStyles.success,
        },
        // Error toast default options
        error: {
            duration: 4000,
            style: toastStyles.error,
        },
        // Custom toast default options
        custom: {
            style: toastStyles.info,
        },
    },
}

// Custom Toaster component with configuration
export const CustomToaster = () => {
    return <Toaster {...toasterConfig} />
}

// Export default toast instance for backward compatibility
export default customToast
